/**
 * 订单状态筛选分段的唯一实现（web 列表页 + 导出共用）。
 *
 * 「在途」是**分段名**，不是订单状态值：它展开成 `orderStatus` 的重复键 `未执行 + 执行中`
 * 发给后端（`ListOrdersQueryDto.orderStatus` 接受 `string | string[]`），永远不会进请求体，
 * 也不会出现在 `PATCH /orders/:id/status` 这类写接口里。所以它只活在这一份表里，
 * 由 `toOrderStatusQuery` 在出网前换成后端认得的值——分段名一旦漏进 query 会被
 * `@IsIn(ORDER_STATUS_VALUES)` 判成 400。
 *
 * 排序跟着分段走，不单独做一组开关：
 *   - 「在途」按订单日期正序：执行中的单档期已开始、日期必然更早，所以「执行中优先」
 *     是这一条排序的自然结果，不需要额外的状态优先级。
 *   - 「全部」按订单日期正序（最早在前），这是需求里点名的口径。
 *   - 其余单档不传排序键，落回后端默认（创建时间倒序）。
 */

/** 分段值：在途（默认视图） */
export const IN_FLIGHT_SEGMENT = '在途';

/** 列表默认视图：只看还在跑的单，「全部」不再当默认 */
export const DEFAULT_ORDER_STATUS_SEGMENT = IN_FLIGHT_SEGMENT;

export interface OrderStatusSegment {
  /** 筛选状态里存的值；'' = 全部 */
  readonly value: string;
  /** 分段按钮上的文案 */
  readonly label: string;
  /** 出网的 `orderStatus`；省略 = 不带这个键 */
  readonly statuses?: readonly string[];
  /** 该分段是否按订单日期正序取数 */
  readonly byStartDateAsc?: boolean;
}

export const ORDER_STATUS_SEGMENTS: readonly OrderStatusSegment[] = [
  { value: IN_FLIGHT_SEGMENT, label: '未执行+执行中', statuses: ['未执行', '执行中'], byStartDateAsc: true },
  { value: '未执行', label: '未执行' },
  { value: '执行中', label: '执行中' },
  { value: '已完成', label: '已完成' },
  { value: '', label: '全部', byStartDateAsc: true },
  { value: '已取消', label: '已取消' },
  { value: '已归档', label: '已归档' },
];

/** 未知取值按默认分段处理，别让一个脏值把列表筛成空。 */
export function orderStatusSegmentOf(value: string): OrderStatusSegment {
  return ORDER_STATUS_SEGMENTS.find(s => s.value === value)
    ?? ORDER_STATUS_SEGMENTS.find(s => s.value === DEFAULT_ORDER_STATUS_SEGMENT)!;
}

/** 「全部」是这一段，判据只在这一处写，免得四端各猜一次。 */
export function isAllOrdersSegment(value: string): boolean {
  return orderStatusSegmentOf(value).value === '';
}

/**
 * 是不是「没在筛选」：默认分段就是列表的常态，不该被算进筛选计数、也不该让空列表
 * 说成「没有符合筛选条件的订单」（新用户一条单都没有时，那句话说反了）。
 */
export function isDefaultOrderStatusSegment(value: string): boolean {
  return orderStatusSegmentOf(value).value === DEFAULT_ORDER_STATUS_SEGMENT;
}

export interface OrderStatusQuery {
  orderStatus?: string | string[];
  orderBy?: string;
  orderDirection?: string;
}

/**
 * 「待审核」徽章是否还成立：订单一旦取消，那张待审邀请就随单作废了
 * （后端 `findAll` 把「待审核 ∧ 已取消」同样排除在待审口径外，首页「待我审核」那格用的就是它）。
 * 徽章继续亮着，等于让人去审一张已经不存在的单。
 * 已通过/已拒绝是对方给过的历史结论，取消不抹掉，照旧显示。
 */
export function reviewBadgeKeyOf(
  reviewStatus: string | null | undefined,
  orderStatus: string | null | undefined,
): string | null {
  if (!reviewStatus) return null;
  if (reviewStatus === '待审核' && orderStatus === '已取消') return null;
  return reviewStatus;
}

/**
 * 分段 → 出网 query 键。数组交给 ofetch 的重复键序列化（实测发成
 * `orderStatus=未执行&orderStatus=执行中`），键不存在就不带——传 `undefined` 上去
 * 会被拼成字面量 `undefined`。
 */
export function toOrderStatusQuery(value: string): OrderStatusQuery {
  const segment = orderStatusSegmentOf(value);
  const query: OrderStatusQuery = {};
  // 单档发字符串、多档发数组，两种都是后端声明过的形状。
  // 没写 statuses 的单档分段（未执行/执行中/已完成/已取消/已归档）就用分段值本身：
  // 它们同时也是 OrderStatus 的库值，`@IsIn` 认得。少发这一格，点了 chip 等于没点——
  // 列表原样不动，但筛选计数与「清除筛选」都把它算成一条生效的筛选。
  const statuses = segment.statuses ?? (segment.value ? [segment.value] : []);
  if (statuses.length === 1) query.orderStatus = statuses[0];
  else if (statuses.length > 1) query.orderStatus = [...statuses];
  if (segment.byStartDateAsc) {
    query.orderBy = 'orderStartDate';
    query.orderDirection = 'asc';
  }
  return query;
}
